import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { FolderOpen, HardDrive, Map as MapIcon, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { create } from "zustand";

import { ACCOUNT_QUOTA_EVENT, STORAGE_FULL_EVENT } from "../api/client";
import type { StorageUsage, Zone } from "../api/types";
import { designateSoon } from "../components/attention";
import { Callout } from "../components/Callout";
import { Dialog, DialogBody } from "../components/Dialog";
import { ProgressBar } from "../components/Gauge";
import { PanelRow, PropRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { Button, IconButton, SectionLabel } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { lang, t } from "../i18n/i18n";
import { formatAreaKm2 } from "../map/zones/format";
import { formatBytes, gaugeLevel } from "../map/zones/storage";
import { STORAGE_KEY, useStorage, useZoneList, ZONE_TARGETS } from "../map/zones/useZones";
import { useUi } from "../state/ui";
import { ZoneDeleteDialog } from "../views/manoeuvre/ZoneDeleteDialog";
import { Waiting } from "../components/Waiting";

/** Espace de stockage de la licence (décision d'Alexis du 02/10/2026, docs/14 §11 question 4) : jauge « Espace
 *  utilisé : x sur y » (menu des réglages de la barre latérale, fiche de la licence, refus 413), fenêtre qui détaille
 *  ce qui l'occupe et désigne de quoi libérer de la place (supprimer des zones ou des dossiers). */

/** « Espace utilisé : 1,2 Go sur 5 Go », d'après les octets (libellés dans la langue de l'interface). */
export function storageText(usage: StorageUsage): string {
  const language = lang();
  return t("Espace utilisé : {used} sur {limit}", {
    used: formatBytes(usage.used_bytes, language),
    limit: formatBytes(usage.limit_bytes, language),
  });
}

/** Entrée du menu des réglages de la barre latérale (dépliée comme en rail) : « Espace utilisé : x sur y » et la
 *  part de l'espace de la licence ; « Espace de stockage · mesure… » tant que la jauge n'est pas lue. */
export function storageMenuEntry(usage: StorageUsage | undefined): { label: string; hint: string } {
  // ui-check: ignore-start — libellé d'attente affiché par <Waiting> là où il est rendu (menu des réglages de la barre latérale)
  if (!usage) return { label: t("Espace de stockage"), hint: t("mesure…") };
  // ui-check: ignore-end
  return { label: storageText(usage), hint: t("{pct} % de l'espace de la licence", { pct: Math.round(gaugeLevel(usage).ratio * 100) }) };
}

/** Jauge : texte et barre (ton d'alerte au-delà de 85 %, d'erreur plein). */
export function StorageGauge({ usage, className }: { usage: StorageUsage; className?: string }) {
  const { ratio, tone } = gaugeLevel(usage);
  const text = storageText(usage);
  return (
    <div className={className}>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-ui text-ink">
        <span className="min-w-0 break-words">{text}</span>
        <span className="shrink-0 text-meta text-ink-muted tabular-nums">{Math.round(ratio * 100)} %</span>
      </div>
      <ProgressBar value={ratio} tone={tone === "ok" ? "accent" : tone} label={text} />
    </div>
  );
}

/** Espace de stockage en bas de la barre latérale dépliée (demande d'Alexis du 28/10/2026) : « Stockage », place
 *  occupée sur l'espace de la licence, barre de progression, puis deux boutons pour libérer de la place : les
 *  terrains libres (fenêtre de l'espace de stockage, où chacun se supprime) et la liste des dossiers. Symbole et
 *  libellé alignés sur les entrées de la barre ; barre et boutons alignés sur le libellé. */
export function SidebarStorage() {
  const usage = useStorage();
  const show = useStorageDialog((s) => s.show);
  const openWelcome = useUi((s) => s.openWelcome);
  const data = usage.data;
  const language = lang();
  const level = data ? gaugeLevel(data) : null;
  const text = data ? storageText(data) : t("Espace de stockage");
  return (
    <div className="px-3 pt-0.5 pb-1.5" data-sidebar-storage="">
      <button
        type="button"
        onClick={() => show()}
        title={data ? t("{texte} : détail de l'espace de stockage", { texte: text }) : undefined}
        className="-mx-3 flex h-7 w-[calc(100%+1.5rem)] items-center gap-2.5 rounded-lg px-3 text-ui text-ink transition-colors hover:bg-hover"
      >
        <HardDrive size={16} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left">{t("Stockage")}</span>
        <span className="shrink-0 text-meta text-ink-muted tabular-nums">
          {data ? (
            t("{used} sur {limit}", {
              used: formatBytes(data.used_bytes, language),
              limit: formatBytes(data.limit_bytes, language),
            })
          ) : (
            <Waiting>{t("mesure…")}</Waiting>
          )}
        </span>
      </button>
      <div className="ml-[26px]">
        <ProgressBar value={level?.ratio ?? 0} tone={!level || level.tone === "ok" ? "accent" : level.tone} label={text} />
        <div className="mt-1.5 flex gap-1.5">
          <Button size="sm" variant="secondary" className="flex-1" title={t("Supprimer des terrains libres")} onClick={() => show()}>
            <MapIcon size={14} /> {t("Terrains")}
          </Button>
          <Button size="sm" variant="secondary" className="flex-1" title={t("Supprimer des dossiers")} onClick={openWelcome}>
            <FolderOpen size={14} /> {t("Dossiers")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Fenêtre de l'espace de stockage, ouverte depuis le menu des réglages, la fiche de la licence ou un refus 413. */
export const useStorageDialog = create<{ open: boolean; designate: boolean; show: (designate?: boolean) => void; hide: () => void }>()(
  (set) => ({
    open: false,
    designate: false,
    show: (designate = false) => set({ open: true, designate }),
    hide: () => set({ open: false, designate: false }),
  }),
);

/** Refus 413 dans le contenu (création de zone) : message, jauge et « Libérer de l'espace », désigné. */
export function StorageCallout({ message }: { message: string }) {
  const { data: usage } = useStorage();
  const show = useStorageDialog((s) => s.show);
  useEffect(() => {
    designateSoon(`[data-attention="${ZONE_TARGETS.storage}"]`);
  }, [message]);
  return (
    <Callout tone="danger" title={message}>
      {usage && <StorageGauge usage={usage} className="mt-1.5" />}
      <div className="mt-2">
        <Button data-attention={ZONE_TARGETS.storage} variant="secondary" size="sm" onClick={() => show(true)}>
          <HardDrive size={14} /> {t("Libérer de l'espace")}
        </Button>
      </div>
    </Callout>
  );
}

/** Refus 413 de l'espace de stockage, où qu'il survienne (`STORAGE_FULL_EVENT`) : la jauge est relue et l'avis
 *  propose « Libérer de l'espace », qui ouvre la fenêtre de l'espace et y désigne ce qui occupe le plus de place. */
export function storageFullNotice(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: STORAGE_KEY });
  useUi.getState().notify({
    text: t("Espace de stockage de la licence plein"),
    detail: t("Supprimez des terrains ou des dossiers pour enregistrer à nouveau."),
    tone: "error",
    action: { label: t("Libérer de l'espace"), run: () => useStorageDialog.getState().show(true) },
  });
}

/** Refus 413 du plafond de stockage d'un compte (comptes nominatifs, docs/17 §3.11), où qu'il survienne : la jauge est
 *  relue ; l'avis donne le refus du serveur (traduit) et « Libérer de l'espace » ouvre la jauge détaillée par compte. */
export function accountQuotaNotice(queryClient: QueryClient, message: string | undefined): void {
  void queryClient.invalidateQueries({ queryKey: STORAGE_KEY });
  useUi.getState().notify({
    text: t("Plafond de stockage du compte atteint"),
    detail: message,
    tone: "error",
    action: { label: t("Libérer de l'espace"), run: () => useStorageDialog.getState().show(true) },
  });
}

/** Hôte de la fenêtre (monté une fois, dans `App`) ; écoute aussi les refus 413 de toute l'application
 *  (`storageFullNotice`, `accountQuotaNotice`). */
export function StorageDialogHost() {
  const open = useStorageDialog((s) => s.open);
  const queryClient = useQueryClient();
  useEffect(() => {
    const onFull = () => storageFullNotice(queryClient);
    const onQuota = (event: Event) => accountQuotaNotice(queryClient, (event as CustomEvent<{ message?: string }>).detail?.message);
    window.addEventListener(STORAGE_FULL_EVENT, onFull);
    window.addEventListener(ACCOUNT_QUOTA_EVENT, onQuota);
    return () => {
      window.removeEventListener(STORAGE_FULL_EVENT, onFull);
      window.removeEventListener(ACCOUNT_QUOTA_EVENT, onQuota);
    };
  }, [queryClient]);
  return <Presence show={open}>{open && <StorageDialog />}</Presence>;
}

function StorageDialog() {
  const hide = useStorageDialog((s) => s.hide);
  const designateTarget = useStorageDialog((s) => s.designate);
  const usage = useStorage();
  const zones = useZoneList();
  const openWelcome = useUi((s) => s.openWelcome);
  const [deleting, setDeleting] = useState<Zone | null>(null);
  const language = lang();
  const data = usage.data;
  // Ce qui occupe le plus de place est désigné : les zones, ou la liste des dossiers.
  useEffect(() => {
    if (!designateTarget || !data) return;
    designateSoon(data.parts.zones >= data.parts.dossiers ? '[data-attention="storage-zones"]' : '[data-attention="storage-dossiers"]');
  }, [designateTarget, data]);

  return (
    <>
      <Dialog
        title={t("Espace de stockage de la licence")}
        width={460}
        onClose={hide}
        actions={<HelpButton screen="poste" topic="poste.stockage" buttonSize="sm" />}
      >
        <DialogBody>
          {data ? (
            <>
              <StorageGauge usage={data} />
              <div>
                <PropRow label={t("Dossiers")}>{formatBytes(data.parts.dossiers, language)}</PropRow>
                <PropRow label={t("Ordres de bataille construits")}>{formatBytes(data.parts.odbs, language)}</PropRow>
                <PropRow label={t("Terrains libres")}>{formatBytes(data.parts.zones, language)}</PropRow>
                {Boolean(data.parts.imports) && (
                  <PropRow label={t("Imports d'OPO en cours")}>{formatBytes(data.parts.imports ?? 0, language)}</PropRow>
                )}
              </div>
              {/* Comptes nominatifs (docs/17 §3.11) : ce que le compte possède, ce qu'il lit d'autre, le reste regroupé. */}
              {data.accounts && (
                <div className="border-t border-line pt-1.5">
                  <PropRow label={t("Vos éléments")}>{formatBytes(data.accounts.mine_bytes, language)}</PropRow>
                  <PropRow label={t("Éléments de l'unité et partagés")}>{formatBytes(data.accounts.readable_bytes, language)}</PropRow>
                  <PropRow label={t("Autres comptes")}>{formatBytes(data.accounts.others_bytes, language)}</PropRow>
                  {data.accounts.quota_bytes !== null && (
                    <PropRow label={t("Plafond de votre compte")}>{formatBytes(data.accounts.quota_bytes, language)}</PropRow>
                  )}
                </div>
              )}
              <Presence show={Boolean(data.accounts?.top_accounts?.length)}>
                {data.accounts?.top_accounts?.length ? (
                  <Callout tone="warn">
                    {t("Licence remplie à plus de 90 % : comptes qui occupent le plus de place — {comptes}.", {
                      comptes: data.accounts.top_accounts.map((a) => `${a.name} (${formatBytes(a.bytes, language)})`).join(", "),
                    })}
                  </Callout>
                ) : null}
              </Presence>
            </>
          ) : usage.isError ? (
            <Callout tone="danger">{t("Espace de stockage indisponible : réessayez dans un instant.")}</Callout>
          ) : (
            <p className="text-ui text-ink-muted">{<Waiting>{t("Mesure de l'espace utilisé…")}</Waiting>}</p>
          )}
          <div data-attention="storage-zones" className="rounded-lg">
            <SectionLabel>{t("Terrains libres de la licence")}</SectionLabel>
            <Presence show={Boolean(zones.data)}>
              <Motion origin="fade" className="mt-1 space-y-px">
                {zones.data?.length ? (
                  zones.data.map((zone) => (
                    <PanelRow
                      key={zone.id}
                      icon={<MapIcon size={14} />}
                      title={zone.name}
                      detail={zone.area_km2 !== null ? formatAreaKm2(zone.area_km2) : undefined}
                      actions={
                        <IconButton
                          size="xs"
                          title={t("Supprimer")}
                          aria-label={t("Supprimer le terrain {name}", { name: zone.name })}
                          onClick={() => setDeleting(zone)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      }
                    />
                  ))
                ) : (
                  <p className="px-2 py-1.5 text-meta text-ink-muted">{t("Aucun terrain libre.")}</p>
                )}
              </Motion>
            </Presence>
          </div>
          <div className="py-2.5">
            <Button
              data-attention="storage-dossiers"
              variant="secondary"
              onClick={() => {
                hide();
                openWelcome();
              }}
            >
              <FolderOpen size={14} /> {t("Ouvrir la liste des dossiers")}
            </Button>
          </div>
        </DialogBody>
      </Dialog>
      {/* Confirmation hors de la fenêtre (plan 90) : une fenêtre animée par transformation en rognerait le voile. */}
      <Presence show={deleting !== null}>{deleting && <ZoneDeleteDialog zone={deleting} onClose={() => setDeleting(null)} />}</Presence>
    </>
  );
}
